import React, { useState } from 'react';
import { useBeats } from '../../../hooks/useBeats';
import { Pill } from '../Pill';
import { PlannedActual } from '../PlannedActual';
import { StageLayout } from '../StageLayout';
import { Portrait } from '../Portrait';
import { peerShare, pollOptions, portraits } from '../../../data/tracking';
import type { PollOption } from '../../../data/tracking';
interface Page2ViewProps {
  /** Holds the story while the viewer answers and reads the peer figure. */
  onHold: (held: boolean) => void;
  onContinue: () => void;
}
const BEATS = [900];

/**
 * The clinician's view. The three answers sit openly on the page; answering
 * reveals only how many peers landed on the same one.
 */
export function Page2View({
  onHold,
  onContinue
}: Page2ViewProps) {
  const beat = useBeats(BEATS);
  const [answer, setAnswer] = useState<PollOption | null>(null);
  const choose = (option: PollOption) => {
    setAnswer(option);
    onHold(true);
  };
  return <StageLayout copy={<>
          <h2 className="max-w-[420px] font-headline text-[24px] font-extrabold leading-[1.1] text-white opacity-0-init animate-fade-in-up md:text-[40px]" data-id="element-175" data-mp="3ux8x3exadv4q">
            When tracking is off, where do you look first?
          </h2>

          <div className={`mt-5 w-full max-w-[340px] transition-[opacity,transform] duration-200 ease-out ${beat >= 1 ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'}`} data-id="element-176" data-mp="7qg7lny7jt3k">
            <ul className="space-y-2" role="group" aria-label="Your view" data-id="element-177" data-mp="hocs36d1c0ec">
              {pollOptions.map(option => {
          const selected = answer === option;
          const optionShare = peerShare[option];
          return <li key={option} data-id="element-178" data-mp="vgizo424gz26">
                    <button onClick={() => choose(option)} aria-pressed={selected} className={`relative w-full overflow-hidden rounded-xl border px-5 py-2.5 text-left font-body text-sm transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.99] ${selected ? 'border-solventum-primary text-white' : answer ? 'border-white/15 text-white/60' : 'border-solventum-primary/45 bg-white/[0.06] text-white/85 hover:border-solventum-primary hover:bg-white/[0.1]'}`} data-id="element-179" data-mp="vjok35zt3crk">
                      {answer && <span aria-hidden="true" className={`absolute inset-y-0 left-0 transition-[width] duration-300 ease-out ${selected ? 'bg-solventum-primary/20' : 'bg-white/[0.07]'}`} style={{
                width: optionShare !== null ? `${optionShare}%` : '0%'
              }} data-id="element-180" data-mp="3g79aeqoojek3" />}
                      <span className="relative flex items-center justify-between gap-3" data-id="element-181" data-mp="fa716rtigtc9">
                        {option}
                        {answer && <span className={`font-headline text-sm font-bold tabular-nums ${selected ? 'text-solventum-primary' : 'text-white/45'}`} data-id="element-182" data-mp="5lfeay3b4z87">
                            {optionShare !== null ? `${optionShare}%` : 'XX%'}
                          </span>}
                      </span>
                    </button>
                  </li>;
        })}
            </ul>

            {answer && <div className="mt-3 animate-rise-in" data-id="element-183" data-mp="1h141nqkh409j">
                <p className="font-body text-xs leading-snug text-white/55" data-id="element-184" data-mp="1gyhuhs1hvmru">
                  Peer responses — you chose{' '}
                  <span className="text-solventum-primary" data-id="element-185" data-mp="391vm6eh3ocl7">
                    {answer.toLowerCase()}
                  </span>
                  .
                </p>
                <p className="mt-1 font-body text-[10px] text-white/30" data-id="element-186" data-mp="345euxbelti3w">
                  Live peer data pending.
                </p>
                <div className="mt-4" data-id="element-187" data-mp="2u5o3xjp95l8k">
                  <Pill onClick={onContinue} data-id="element-188" data-mp="2u6pdtkuj81op">Continue →</Pill>
                </div>
              </div>}
          </div>
        </>} visual={answer ? <figure className="w-full max-w-[340px] animate-fade-in" data-id="element-189" data-mp="2ldm5fqyquf0x">
            <div className="h-40 w-full overflow-hidden rounded-xl md:h-56" data-id="element-190" data-mp="2ge9jr9jlzdzx">
              <Portrait src={portraits.peers.image} alt={portraits.peers.alt} fade="none" data-id="element-191" data-mp="2ben64yzvbvql" />
            </div>
            <figcaption className="mt-2 font-body text-[11px] text-white/40" data-id="element-192" data-mp="26gbvim1r1h7b">
              Clinicians answering the same question.
            </figcaption>
          </figure> : <PlannedActual data-id="element-193" data-mp="21952d0k9xxnn" />} data-id="element-174" data-mp="2mef7uigm8qcj" />;
}